import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import TierChip from '../Ladder/TierChip';
import TraceLink from '../agent-common/TraceLink';
import { knowledgeBesideNumbers, type LadderAnswer, type SpecialistBlock } from '../Ladder/claims';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { followUpOf } from '../../stores/slices/landingAgentSlice';
import { createAnnotation } from '../../apis';

const day = (iso?: string | null) => (iso ? iso.slice(0, 10) : null);
// a note's title: its first line, as the number popover titles one
const noteTitle = (text: string) => { const first = text.trim().split(/\r?\n/)[0] || 'Note'; return first.length > 80 ? `${first.slice(0, 79)}…` : first; };

/** What people said (#764): the topics with their summary for this viewer, the quotes as written with who said them,
 *  the decisions, and how many items sit under their bar — a knowledge answer's body, and the Knowledge section beside
 *  a list of earlier analyses (#911). */
export function KnowledgeSections({ block }: { block: Omit<SpecialistBlock, 'kind'> }) {
  return (
    <>
      {!!block.topics?.length && (
        <Box data-testid="specialist-topics">
          <Typography variant="caption" color="text.secondary">Discussions</Typography>
          {block.topics.map((t) => (
            <Box key={t.id} sx={{ mt: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.title}</Typography>
              {t.summary && <Typography variant="body2" color="text.secondary">{t.summary}</Typography>}
            </Box>
          ))}
        </Box>
      )}
      {!!block.quotes?.length && (
        <Box data-testid="specialist-quotes">
          <Typography variant="caption" color="text.secondary">Who said what</Typography>
          {block.quotes.map((q) => (
            <Box key={`${q.span.document_id}:${q.span.start}`} component="blockquote" sx={{ m: 0, mt: 0.75, pl: 1.5, borderLeft: 3, borderColor: 'divider' }}>
              <Typography variant="body2">“{q.text}”</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">{q.where || q.speaker || 'unattributed'}</Typography>
                <TierChip tier={q.tier} />
              </Stack>
            </Box>
          ))}
        </Box>
      )}
      {!!block.decisions?.length && (
        <Box data-testid="specialist-decisions">
          <Typography variant="caption" color="text.secondary">Decisions</Typography>
          {block.decisions.map((d) => (
            <Typography key={d.id} variant="body2">{d.text}{d.decided_by ? ` — ${d.decided_by}` : ''}{day(d.decided_at) ? `, ${day(d.decided_at)}` : ''}</Typography>
          ))}
        </Box>
      )}
      {!!block.below_bar && (
        <Typography variant="caption" color="text.secondary">
          {block.below_bar === 1 ? '1 item' : `${block.below_bar} items`} in what you can read sit under your evidence bar.
        </Typography>
      )}
    </>
  );
}

/** "Something else" on a doubt's reply (#1671): what the asker expected, saved as a note (kind `note`) on the earlier
 *  answer, as the number popover's Annotate saves one — never sent as a question. */
function NoteOffer({ text, context }: { text: string; context?: SpecialistBlock['context'] }) {
  const [open, setOpen] = React.useState(false);
  const [body, setBody] = React.useState('');
  const [state, setState] = React.useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const save = async () => {
    if (!body.trim()) return;
    setState('saving');
    try {
      const end = context?.window?.end || new Date().toISOString().slice(0, 10);
      await createAnnotation({
        kind: 'note', ts: `${end.slice(0, 10)}T00:00:00Z`, title: noteTitle(body), body: body.trim(), source: 'note',
        ...(context?.session_id ? { source_ref: `check:${context.session_id}` } : {}),
        scope: { ...(context?.metrics?.length ? { metrics: context.metrics } : {}) }, confidence: 1,
      });
      setState('saved');
    } catch {
      setState('failed');
    }
  };
  if (state === 'saved') return <Typography variant="caption" color="text.secondary">Saved as a note on that answer.</Typography>;
  if (!open) return <Button size="small" variant="outlined" onClick={() => setOpen(true)}>{text}</Button>;
  return (
    <Stack direction="row" spacing={1} sx={{ width: '100%', alignItems: 'center' }}>
      <TextField size="small" fullWidth autoFocus placeholder="What did you expect to see?" value={body}
        onChange={(e) => setBody(e.target.value)} inputProps={{ 'aria-label': 'What did you expect to see?' }} />
      <Button size="small" variant="contained" disabled={!body.trim() || state === 'saving'} onClick={save}>Save note</Button>
      {state === 'failed' && <Typography variant="caption" color="error">Could not save the note</Typography>}
    </Stack>
  );
}

/** A knowledge, help or decline answer (#764): what people said — the topics with their summary for this viewer, the
 *  quotes as written with who said them, the decisions — or a help article, or the strict decline with its offers. */
export function SpecialistAnswer({ ladder, onAsk, sessionId }: { ladder: LadderAnswer; onAsk?: (question: string) => void; sessionId?: string | null }) {
  const block = ladder.specialist as SpecialistBlock;
  // the decline's sentence has one source: the block it was written from
  const headline = block.kind === 'decline' && block.message ? block.message : ladder.what?.headline;
  const offers = block.offers || [];
  // a Writer's reply (#1667) is its words, as written; with none (a code reply, or a reader who may not see them) the
  // headline code wrote stands in their place
  const words = block.kind === 'talk' ? (block.words || []).filter((w) => w && w.trim()) : [];
  return (
    <Box data-testid={`specialist-${block.kind}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, display: 'grid', gap: 1.5 }}>
      {block.kind === 'talk' && !!words.length ? (
        <Box data-testid="talk-words" sx={{ display: 'grid', gap: 1 }}>
          {words.map((w, i) => <Typography key={i} variant="body1">{w}</Typography>)}
        </Box>
      ) : headline && <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{headline}</Typography>}
      {block.kind === 'talk' && !!block.cites?.length && (
        <Box component="ul" data-testid="talk-cites" sx={{ m: 0, pl: 2.5 }}>
          {block.cites.map((c) => <Typography key={c} component="li" variant="body2" color="text.secondary">{c}</Typography>)}
        </Box>
      )}
      {block.kind === 'decline' && block.detail && (
        <Typography variant="body2" data-testid="decline-detail">{block.detail}</Typography>
      )}
      {block.kind === 'help' && block.article && (
        <Box>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{block.article.answer}</Typography>
          <Typography variant="caption" color="text.secondary">Where: {block.article.where}</Typography>
        </Box>
      )}
      {block.kind === 'check' && !!block.computed?.length && (
        <Box data-testid="check-computed">
          <Typography variant="caption" color="text.secondary">How it was computed</Typography>
          {block.computed.map((line) => <Typography key={line} variant="body2">{line}</Typography>)}
        </Box>
      )}
      <KnowledgeSections block={block} />
      {/* every answer carries its trace (#764 r1: the traceability rule), as AnswerCard does */}
      {sessionId && <Box><TraceLink sessionId={sessionId} /></Box>}
      {!!offers.length && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {offers.map((o) => (o.note
            ? <NoteOffer key={o.id || 'note'} text={o.text} context={block.context} />
            : <Button key={o.id || o.question} size="small" variant="outlined" onClick={() => onAsk?.(o.question)} title={o.question}>{o.text}</Button>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default function SpecialistAnswerCard({ turn }: { turn: LandingAgentTurn }) {
  const send = useVoyagerStore((s) => s.sendLandingAgentQuery);
  const ladder = turn.result?.ladder as LadderAnswer | undefined;
  if (!ladder?.specialist || knowledgeBesideNumbers(turn.result)) return null;
  // a doubt's options (#1671) are asked about THAT answer: its scope, as every follow-up about a displayed answer is
  const onAsk = ladder.specialist.kind === 'check' ? (q: string) => send(q, followUpOf(turn)) : (q: string) => send(q);
  return <SpecialistAnswer ladder={ladder} onAsk={onAsk} sessionId={turn.sessionId || ladder.trace_session_id || null} />;
}
